import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconTrash,
  IconRefresh,
} from "@tabler/icons-react";
import {
  useSalesRepsAdmin,
  useCreateSalesRep,
  useUpdateSalesRep,
  useDeleteSalesRep,
} from "../../../hooks/admin/useSalesReps";
import SalesRepForm, {
  SalesRepFormValues,
} from "../../../components/admin/salesreps/SalesRepForm";
import { SalesRep } from "../../../services/admin/salesreps.service";

const ITEMS_PER_PAGE = 20;

export default function SalesRepsAdminPage() {
  // Modal states
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);

  // Page state
  const [editingSalesRep, setEditingSalesRep] = useState<SalesRep | null>(null);
  const [itemToDelete, setItemToDelete] = useState<SalesRep | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);

  // Data hooks
  const { data: salesReps = [], isLoading, refetch } = useSalesRepsAdmin();
  const createSalesRep = useCreateSalesRep();
  const updateSalesRep = useUpdateSalesRep();
  const deleteSalesRep = useDeleteSalesRep();

  // Filter sales reps
  const filteredSalesReps = useMemo(() => {
    if (!debouncedSearch.trim()) return salesReps;

    const query = debouncedSearch.toLowerCase();
    return salesReps.filter(
      (rep) =>
        rep.name?.toLowerCase().includes(query) ||
        rep.address?.toLowerCase().includes(query) ||
        rep.deal?.toLowerCase().includes(query)
    );
  }, [salesReps, debouncedSearch]);

  // Pagination
  const totalPages = Math.ceil(filteredSalesReps.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredSalesReps.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredSalesReps, page]);

  // Handlers
  const handleAdd = () => {
    setEditingSalesRep(null);
    openModal();
  };

  const handleEdit = (salesRep: SalesRep) => {
    setEditingSalesRep(salesRep);
    openModal();
  };

  const handleSubmit = async (values: SalesRepFormValues) => {
    if (editingSalesRep) {
      await updateSalesRep.mutateAsync({
        id: editingSalesRep.id,
        ...values,
      });
    } else {
      await createSalesRep.mutateAsync(values);
    }
    closeModal();
  };

  const handleDeleteClick = (salesRep: SalesRep) => {
    setItemToDelete(salesRep);
    openDeleteModal();
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      await deleteSalesRep.mutateAsync(itemToDelete.id);
    }
    closeDeleteModal();
    setItemToDelete(null);
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Sales Representatives</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage sales representatives and their deal information
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add Sales Rep
            </Button>
          </Group>

          {/* Search Section */}
          <Paper p="md" withBorder>
            <Stack gap="sm">
              <Group justify="space-between">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <Button
                  variant="subtle"
                  size="compact-sm"
                  onClick={() => refetch()}
                  loading={isLoading}
                  leftSection={<IconRefresh size={16} />}
                />
              </Group>
              <TextInput
                placeholder="Search by name, address, or deal..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                leftSection={<IconSearch size={16} />}
              />
              <Text size="sm" c="dimmed">
                {filteredSalesReps.length} sales rep
                {filteredSalesReps.length !== 1 ? "s" : ""} found
              </Text>
            </Stack>
          </Paper>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header with Pagination */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Sales Representatives ({filteredSalesReps.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(
                        page * ITEMS_PER_PAGE,
                        filteredSalesReps.length
                      )}{" "}
                      of {filteredSalesReps.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredSalesReps.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {search
                    ? "No sales reps match the current search"
                    : "No sales reps found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Address</Table.Th>
                      <Table.Th>Deal</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((salesRep) => (
                      <Table.Tr key={salesRep.id}>
                        {/* Name */}
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {salesRep.name}
                          </Text>
                        </Table.Td>

                        {/* Address */}
                        <Table.Td>
                          <Text size="sm" lineClamp={2}>
                            {salesRep.address || "—"}
                          </Text>
                        </Table.Td>

                        {/* Deal */}
                        <Table.Td>
                          <Text size="sm" lineClamp={2}>
                            {salesRep.deal || "—"}
                          </Text>
                        </Table.Td>

                        {/* Actions */}
                        <Table.Td>
                          <Group gap="xs" wrap="nowrap">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(salesRep)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                onClick={() => handleDeleteClick(salesRep)}
                              >
                                <IconTrash size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Add/Edit Modal */}
        <Modal
          opened={modalOpened}
          onClose={closeModal}
          title={editingSalesRep ? "Edit Sales Rep" : "Add New Sales Rep"}
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <SalesRepForm
              key={editingSalesRep?.id || "new"}
              initialValues={editingSalesRep || undefined}
              onSubmit={handleSubmit}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createSalesRep.isPending || updateSalesRep.isPending}
                onClick={() => {
                  const form = document.querySelector("form");
                  if (form) form.requestSubmit();
                }}
              >
                {editingSalesRep ? "Update" : "Create"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          opened={deleteModalOpened}
          onClose={closeDeleteModal}
          title="Delete Sales Rep"
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to delete{" "}
              <strong>{itemToDelete?.name}</strong>?
            </Text>
            <Text size="sm" c="dimmed">
              This action cannot be undone.
            </Text>
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeDeleteModal}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={confirmDelete}
                loading={deleteSalesRep.isPending}
              >
                Delete
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
